/**
 * Mock Template Blocks for BlockTemplatePanel
 *
 * Provides real StrategyBlock instances for each blockType from schema.
 * These serve as template examples for building strategy flows.
 *
 * Usage:
 * - BlockTemplatePanel: Display draggable templates
 * - BlockPreviewRenderer: Render visual previews
 *
 * Sprint: sprint-7
 * Task: T005.1.1
 * Requirement: FR-P5-005 - Template Panel shows blocks in blocks mode
 */

import type { StrategyBlock } from '@segmently/schema-models/strategy';

/**
 * Mock template blocks array
 *
 * Each template represents one blockType with realistic data.
 * Total: 10 templates covering all block types from schema.
 */
export const MOCK_TEMPLATE_BLOCKS: Partial<StrategyBlock>[] = [
  // 1. Welcome - Introduction block
  {
    id: 'template-block-welcome',
    entityId: 'template-block-welcome',
    name: 'Welcome Block',
    blockType: 'welcome',
    description: 'Introduce users to the app and set expectations',
    order: 1,
    position: { x: 0, y: 0 },
    action: { kind: 'Next', edges: [] },
  },

  // 2. User Goals - Goal selection
  {
    id: 'template-block-user-goals',
    entityId: 'template-block-user-goals',
    name: 'User Goals',
    blockType: 'user_goals',
    description: 'Collect user primary goals and objectives',
    order: 2,
    position: { x: 0, y: 0 },
    action: { kind: 'Next', edges: [] },
  },

  // 3. User Data - Data collection
  {
    id: 'template-block-user-data',
    entityId: 'template-block-user-data',
    name: 'User Data Collection',
    blockType: 'user_data',
    description: 'Gather essential user information (age, height, weight, etc.)',
    order: 3,
    position: { x: 0, y: 0 },
    action: { kind: 'Next', edges: [] },
  },

  // 4. Plan/Paywall - Subscription
  {
    id: 'template-block-plan-paywall',
    entityId: 'template-block-plan-paywall',
    name: 'Plan & Paywall',
    blockType: 'plan_paywall',
    description: 'Present personalized plan and subscription options',
    order: 4,
    position: { x: 0, y: 0 },
    action: { kind: 'Next', edges: [] },
  },

  // 5. History - User background
  {
    id: 'template-block-history',
    entityId: 'template-block-history',
    name: 'User History',
    blockType: 'history',
    description: 'Collect user history and past experiences',
    order: 5,
    position: { x: 0, y: 0 },
    action: { kind: 'Next', edges: [] },
  },

  // 6. Motivation - Motivational content
  {
    id: 'template-block-motivation',
    entityId: 'template-block-motivation',
    name: 'Motivation Block',
    blockType: 'motivation',
    description: 'Inspire and motivate users to continue',
    order: 6,
    position: { x: 0, y: 0 },
    action: { kind: 'Next', edges: [] },
  },

  // 7. Environment - Context assessment
  {
    id: 'template-block-environment',
    entityId: 'template-block-environment',
    name: 'Environment Assessment',
    blockType: 'environment',
    description: 'Understand user living and working environment',
    order: 7,
    position: { x: 0, y: 0 },
    action: { kind: 'Next', edges: [] },
  },

  // 8. Habits - Habit formation
  {
    id: 'template-block-habits',
    entityId: 'template-block-habits',
    name: 'Habits Block',
    blockType: 'habits',
    description: 'Identify and build healthy habits',
    order: 8,
    position: { x: 0, y: 0 },
    action: { kind: 'Next', edges: [] },
  },

  // 9. Strategy - Strategic planning
  {
    id: 'template-block-strategy',
    entityId: 'template-block-strategy',
    name: 'Strategy Block',
    blockType: 'strategy',
    description: 'Create personalized strategy and action plan',
    order: 9,
    position: { x: 0, y: 0 },
    action: { kind: 'Next', edges: [] },
  },

  // 10. Custom - Generic block
  {
    id: 'template-block-custom',
    entityId: 'template-block-custom',
    name: 'Custom Block',
    blockType: 'custom',
    description: 'Custom block for specific use cases',
    order: 10,
    position: { x: 0, y: 0 },
    action: { kind: 'Next', edges: [] },
  },
];

/**
 * Get mock template block by blockType
 *
 * @param blockType - The block type to find
 * @returns Mock block instance or undefined if not found
 */
export function getMockTemplateByType(blockType: string): Partial<StrategyBlock> | undefined {
  return MOCK_TEMPLATE_BLOCKS.find(block => block.blockType === blockType);
}

/**
 * Get all available block types from mock templates
 *
 * @returns Array of block type strings
 */
export function getAvailableBlockTypes(): string[] {
  return MOCK_TEMPLATE_BLOCKS.map(block => block.blockType || 'unknown');
}

/**
 * Alias for backward compatibility
 */
export const mockTemplateBlocks = MOCK_TEMPLATE_BLOCKS;
